// Shared "review + sign a franchise contract" panel — used by BOTH the
// Hub-authenticated /portal/contracts modal (``PortalContractsPage``) and
// the token-gated Launch Portal signing surface
// (``LaunchPortalContractSection``). Extracted in [22] so any change to
// the signing UX lands in one place.
//
// Owns the local signing state (checkbox, signature pad, viewer-broken
// latch, submit spinner + error) and exposes
// ``onAccept({checkbox_confirmed, territory_confirmed,
// signature_png_b64})`` to the caller. The caller does the network
// call, then hands back an updated contract row on success.
//
// Feb 2026 (Sam): the read-to-end scroll / final-page gate has been
// REMOVED from both surfaces. The gate created friction without
// proving the contract had actually been read — franchisees also
// download the PDF and read outside the browser. Signing now unlocks
// on the three genuine requirements: territory-confirm (where
// applicable), the acceptance-wording checkbox, and a drawn
// signature. The wording, acceptance payload, audit record and
// backend validation are all untouched.
import { useCallback, useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  AlertTriangle, CheckCircle2, ExternalLink, Loader2,
} from "lucide-react";

import ContractPdfViewer from "@/components/contracts/ContractPdfViewer";
import DualAcceptancePanel from "@/components/contracts/DualAcceptancePanel";

const ACCEPTANCE_WORDING =
  "I confirm that I have read and agree to the terms of this franchise agreement.";
const TERRITORY_WORDING =
  "I confirm the agreed territory area shown on the map is correct.";
const TERRITORY_LINK_DEFAULT = "View my agreed territory map";

export default function ContractReviewAndSign({
  contract,
  pdfUrl,
  pdfFileHeaders,
  downloadUrl,
  onAccept,
  // Optional slots so surface-specific chrome (status pill, close btn)
  // can render above/below the shared body without pulling it in here.
  header,
  signedNoticeExtra,
  testidPrefix = "contract-review-and-sign",
  viewerMinHeight = 560,
  // Launch Portal v2 opt-in: paginated PDF viewer with Prev/Next.
  // Default false → HUB /portal/contracts keeps its scroll viewer.
  // Note: this flag NO LONGER affects the signing gate — it only
  // affects how the PDF pages themselves render inside the viewer.
  viewerPaginated = false,
  // Only Hub My Contracts can complete two-person confirmations.
  allowDualAcceptance = false,
  onDualSigned,
}) {
  const isSigned = contract.status === "signed";
  const isDual = contract.signing_mode === "dual_confirmation";
  const hasSignatureAnchor = contract.has_signature_anchor !== false;
  const requiresTerritoryConfirm = Boolean(contract.frozen_territory_map_url);

  const [checkbox, setCheckbox] = useState(false);
  const [territoryConfirmed, setTerritoryConfirmed] = useState(false);
  const [viewerBroken, setViewerBroken] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signErr, setSignErr] = useState("");
  const sigPadRef = useRef(null);
  const [sigEmpty, setSigEmpty] = useState(true);

  // Stable callback identity across renders — passed into the viewer
  // so a checkbox tick / signature stroke doesn't trigger a viewer
  // remount. See the [21f] commit message for the full failure
  // trace; the same rule still applies even now that the viewer's
  // callback is a no-op.
  const handleViewerError = useCallback(() => setViewerBroken(true), []);
  const fileName = useMemo(
    () => `${contract.contract_reference || contract.id || "contract"}.pdf`,
    [contract.contract_reference, contract.id],
  );

  const territoryOk = !requiresTerritoryConfirm || territoryConfirmed;
  const canAccept =
    checkbox && !sigEmpty && territoryOk &&
    !signing && !viewerBroken && hasSignatureAnchor &&
    contract.status === "issued";

  const lockedReasons = [];
  if (!hasSignatureAnchor) lockedReasons.push("this contract needs to be reissued by HQ");
  if (viewerBroken) lockedReasons.push("PDF viewer failed — refresh the page");
  if (requiresTerritoryConfirm && !territoryConfirmed) lockedReasons.push("confirm the territory area");
  if (!checkbox) lockedReasons.push("tick the confirmation box");
  if (sigEmpty && hasSignatureAnchor) lockedReasons.push("draw your signature");

  async function handleAccept() {
    setSignErr("");
    setSigning(true);
    let signaturePngB64;
    try {
      const pad = sigPadRef.current;
      if (!pad || pad.isEmpty?.()) {
        setSignErr("Please draw your signature to sign.");
        setSigning(false);
        return;
      }
      const canvas = pad.getCanvas?.();
      if (!canvas || typeof canvas.toDataURL !== "function") {
        throw new Error("Signature pad canvas is not available. Please refresh and try again.");
      }
      signaturePngB64 = canvas.toDataURL("image/png");
      if (!signaturePngB64 || !signaturePngB64.startsWith("data:image/")) {
        throw new Error("Signature could not be captured. Please draw again.");
      }
    } catch (e) {
      setSignErr(e.message || "Could not capture your signature.");
      setSigning(false);
      return;
    }
    try {
      await onAccept({
        checkbox_confirmed: true,
        territory_confirmed: territoryConfirmed || !requiresTerritoryConfirm,
        signature_png_b64: signaturePngB64,
      });
    } catch (e) {
      setSignErr(
        e?.response?.data?.detail || e?.message || "Signing failed. Please try again.",
      );
    } finally {
      setSigning(false);
    }
  }

  return (
    <div data-testid={testidPrefix}>
      {header}
      <div className="grid grid-cols-1 md:grid-cols-3 min-h-0" style={{ minHeight: viewerMinHeight }}>
        <div className="md:col-span-2 border-r min-h-0 flex flex-col">
          {pdfUrl ? (
            <ContractPdfViewer
              fileUrl={pdfUrl}
              fileHeaders={pdfFileHeaders}
              downloadUrl={downloadUrl}
              fileName={fileName}
              onViewerError={handleViewerError}
              minHeight={viewerMinHeight}
              // Dual contracts use the Launch Portal page-by-page viewer, which works inside the Hub modal.
              paginated={viewerPaginated || isDual}
              startAtLastPage={isDual && isSigned && Boolean(contract.just_accepted)}
            />
          ) : (
            <div className="p-8 text-stone-500 text-sm flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading contract PDF…
            </div>
          )}
        </div>
        <div className="p-5 overflow-auto">
          {isSigned ? (
            <div
              className="text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm flex items-start gap-2"
              data-testid={`${testidPrefix}-signed-notice`}
            >
              <CheckCircle2 className="h-4 w-4 mt-0.5" />
              <div>
                <div className="font-semibold">
                  Signed on {contract.signed_at ? new Date(contract.signed_at).toLocaleString("en-GB") : "—"}
                </div>
                {signedNoticeExtra}
              </div>
            </div>
          ) : isDual && allowDualAcceptance ? (
            <DualAcceptancePanel row={contract} viewerBroken={viewerBroken}
              onSigned={(updated) => onDualSigned?.({ ...updated, just_accepted: true })} />
          ) : isDual ? (
            <div
              className="text-amber-900 bg-amber-50 border border-amber-300 rounded-md p-4 text-sm"
              data-testid={`${testidPrefix}-dual-use-hub`}
            >
              <div className="font-semibold mb-1 flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4" />
                Two signatories required
              </div>
              <div className="text-xs mt-1 text-amber-800 leading-relaxed">
                This contract must be accepted by both named people. Please sign in to the
                Creative Mojo Hub and open My Contracts to complete both confirmations.
              </div>
            </div>
          ) : !hasSignatureAnchor ? (
            <div
              className="text-amber-900 bg-amber-50 border border-amber-300 rounded-md p-4 text-sm"
              data-testid={`${testidPrefix}-reissue-required`}
            >
              <div className="font-semibold mb-1 flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4" />
                Contract needs to be reissued
              </div>
              <div className="text-xs mt-1 text-amber-800 leading-relaxed">
                This contract was issued before our new electronic signature
                workflow. Please contact Creative Mojo to have it reissued from
                an updated template.
              </div>
            </div>
          ) : (
            <>
              {requiresTerritoryConfirm && (
                <div
                  className="mb-4 rounded-md border border-sky-200 bg-sky-50 p-3"
                  data-testid={`${testidPrefix}-territory-block`}
                >
                  <div className="text-sm font-semibold text-sky-900 mb-1">
                    Agreed territory
                  </div>
                  <p className="text-xs text-sky-900/80 mb-2">
                    Please review the agreed territory area before signing.
                  </p>
                  <a
                    href={contract.frozen_territory_map_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-800 hover:text-sky-900 underline underline-offset-2"
                    data-testid={`${testidPrefix}-territory-map-link`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {contract.frozen_territory_map_url_display_text || TERRITORY_LINK_DEFAULT}
                  </a>
                  <label
                    className="mt-3 flex items-start gap-2 text-sm cursor-pointer"
                    data-testid={`${testidPrefix}-territory-agree-wrap`}
                  >
                    <input
                      type="checkbox"
                      checked={territoryConfirmed}
                      onChange={(e) => setTerritoryConfirmed(e.target.checked)}
                      className="mt-1"
                      data-testid={`${testidPrefix}-territory-agree-checkbox`}
                    />
                    <span>{TERRITORY_WORDING}</span>
                  </label>
                </div>
              )}

              <h2 className="text-lg font-semibold mb-2">Accept and sign contract</h2>

              <label
                className="flex items-start gap-2 text-sm mb-3 cursor-pointer"
                data-testid={`${testidPrefix}-agree-wrap`}
              >
                <input
                  type="checkbox"
                  checked={checkbox}
                  onChange={(e) => setCheckbox(e.target.checked)}
                  className="mt-1"
                  data-testid={`${testidPrefix}-agree-checkbox`}
                />
                <span>{ACCEPTANCE_WORDING}</span>
              </label>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-stone-700 font-medium">
                    Draw your signature
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      sigPadRef.current?.clear();
                      setSigEmpty(true);
                    }}
                    className="text-xs text-stone-500 hover:text-stone-800 underline"
                    data-testid={`${testidPrefix}-signature-clear-btn`}
                  >
                    Clear signature
                  </button>
                </div>
                <div
                  className="rounded-md border border-stone-300 bg-white touch-none"
                  data-testid={`${testidPrefix}-signature-pad`}
                >
                  <SignatureCanvas
                    ref={sigPadRef}
                    penColor="#0f172a"
                    onEnd={() => setSigEmpty(!!sigPadRef.current?.isEmpty())}
                    canvasProps={{
                      width: 380,
                      height: 140,
                      className: "w-full h-[140px] rounded-md",
                    }}
                  />
                </div>
                <div className="text-[11px] text-stone-500 mt-1">
                  Works with mouse, trackpad, touchscreen and stylus.
                </div>
              </div>

              {signErr && (
                <div
                  className="mb-3 text-xs bg-red-50 border border-red-200 text-red-800 rounded p-2 flex items-start gap-1.5"
                  data-testid={`${testidPrefix}-sign-error`}
                >
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold mb-0.5">Signing failed</div>
                    <div>{signErr}</div>
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={handleAccept}
                disabled={!canAccept}
                data-testid={`${testidPrefix}-accept-btn`}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm border rounded-md bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Accept and sign contract
              </button>
              {!canAccept && !signing && lockedReasons.length > 0 && (
                <div
                  className="mt-2 text-[11px] text-stone-500"
                  data-testid={`${testidPrefix}-locked-hint`}
                >
                  Still needed: {lockedReasons.join(" · ")}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
